import { Fragment, KeyboardEvent, MouseEvent, memo } from 'react';
import { TableCell, TableRow } from '@mui/material';
import { Row } from '@tanstack/react-table';
import MainTableCellBasic from '@components/MainTable/MainTableCellBasic';
import { CustomBodyCellProps } from '@app/types/main-table';

interface MainTableRowProps<TData extends Record<string, any>> {
  row: Row<TData>;
  columnOrder?: string[];
  isLeftScrollable?: boolean;
  isRightScrollable?: boolean;
  components?: {
    bodyCell?: (c: CustomBodyCellProps<TData>) => React.ReactNode;
    expandedComponent?: (c: Row<TData>) => React.ReactNode;
  };
  /** Opens the row when it is clicked anywhere but on a control inside it. */
  onRowClick?: (original: TData) => void;
}

// A click that lands on a control inside the row does that control's job only.
const INTERACTIVE =
  'button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [role="checkbox"], [role="combobox"]';

const isFromControl = (event: MouseEvent<HTMLElement>) => {
  const target = event.target as HTMLElement | null;
  const control = target?.closest?.(INTERACTIVE);
  return !!control && event.currentTarget.contains(control);
};

function MainTableRowComponent<TData extends Record<string, any>>({
  row,
  columnOrder,
  isLeftScrollable,
  isRightScrollable,
  components,
  onRowClick,
}: MainTableRowProps<TData>) {
  // ZLAYT-1579: rows that open a document can be clicked anywhere, not only
  // on the small eye icon.
  const rowProps = onRowClick
    ? {
        hover: true,
        tabIndex: 0,
        sx: { cursor: 'pointer' },
        onClick: (event: MouseEvent<HTMLTableRowElement>) => {
          if (isFromControl(event)) return;
          if (window.getSelection?.()?.toString()) return;
          onRowClick(row.original);
        },
        onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
          if (event.target !== event.currentTarget) return;
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          onRowClick(row.original);
        },
      }
    : {};
  return (
    <>
      <TableRow {...rowProps}>
        {row.getVisibleCells().map((cell) => {
          const bodyCellParam = {
            cell,
            columnOrder,
            isLeftScrollable,
            isRightScrollable,
          };
          return (
            <Fragment key={cell.id}>
              {components?.bodyCell ? (
                components.bodyCell(bodyCellParam)
              ) : (
                <MainTableCellBasic
                  cell={cell}
                  isLeftScrollable={isLeftScrollable}
                  isRightScrollable={isRightScrollable}
                />
              )}
            </Fragment>
          );
        })}
      </TableRow>

      {/* custom-expanded-component start  */}
      {components?.expandedComponent && row.getIsExpanded() && (
        <TableRow>
          <TableCell sx={{ p: 0 }} colSpan={row.getVisibleCells().length}>
            {components.expandedComponent(row)}
          </TableCell>
        </TableRow>
      )}
      {/* customExpandedComponent end */}
    </>
  );
}

// Custom comparison function for MainTableRow memo
const areEqual = <TData extends Record<string, any>>(
  prevProps: MainTableRowProps<TData>,
  nextProps: MainTableRowProps<TData>
) => {
  // Compare row ID (unique identifier)
  if (prevProps.row.id !== nextProps.row.id) {
    return false;
  }

  if (prevProps.onRowClick !== nextProps.onRowClick) {
    return false;
  }

  // Compare expansion state
  if (prevProps.row.getIsExpanded() !== nextProps.row.getIsExpanded()) {
    return false;
  }

  // Compare scroll states
  if (
    prevProps.isLeftScrollable !== nextProps.isLeftScrollable ||
    prevProps.isRightScrollable !== nextProps.isRightScrollable
  ) {
    return false;
  }

  // Compare columnOrder array (shallow comparison - arrays are usually stable)
  if (prevProps.columnOrder !== nextProps.columnOrder) {
    // If both are undefined/null, they're equal
    if (!prevProps.columnOrder && !nextProps.columnOrder) {
      // Continue to next check
    } else if (
      !prevProps.columnOrder ||
      !nextProps.columnOrder ||
      prevProps.columnOrder.length !== nextProps.columnOrder.length
    ) {
      return false;
    } else {
      // Shallow array comparison
      for (let i = 0; i < prevProps.columnOrder.length; i++) {
        if (prevProps.columnOrder[i] !== nextProps.columnOrder[i]) {
          return false;
        }
      }
    }
  }

  // Compare components object by reference (functions are recreated, so this is important)
  // If components changed, we need to re-render
  if (prevProps.components !== nextProps.components) {
    return false;
  }

  // Compare visible cells count (if cells changed, row needs update)
  const prevCells = prevProps.row.getVisibleCells();
  const nextCells = nextProps.row.getVisibleCells();
  if (prevCells.length !== nextCells.length) {
    return false;
  }

  // Compare cell IDs to detect if cells changed
  for (let i = 0; i < prevCells.length; i++) {
    if (prevCells[i].id !== nextCells[i].id) {
      return false;
    }
  }

  // All checks passed - props are equal, skip re-render
  return true;
};

const MainTableRow = memo(
  MainTableRowComponent,
  areEqual
) as typeof MainTableRowComponent;

export default MainTableRow;
